import React, { ReactNode, MouseEvent, useState, useEffect } from "react";
import { X } from "lucide-react";
import Portal from "./Portal";

interface ModalProps {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  children: ReactNode;
}

const Modal: React.FC<ModalProps> = ({ isOpen, setIsOpen, children }) => {
  const [mounted, setMounted] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setMounted(true);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setVisible(true);
        });
      });
    } else {
      setVisible(false);
      const timer = setTimeout(() => setMounted(false), 300);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  if (!mounted) return null;

  const handleBackdropClick = (e: MouseEvent<HTMLDivElement>) => {
    setIsOpen(false);
  };

  const stopPropagation = (e: MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
  };

  return (
    <Portal>
      <div
        className={`fixed inset-0 z-50 flex items-start justify-center overflow-y-auto transition-opacity duration-200 ${
          visible
            ? "bg-black/50 opacity-100"
            : "opacity-0 pointer-events-none"
        }`}
        onClick={handleBackdropClick}
      >
        {/* Modal panel */}
        <div
          className={`relative rounded-lg bg-white border border-gray-200 text-left transition-all duration-300 ease-out w-full sm:max-w-lg mx-4 mb-4 ${
            visible ? "opacity-100 mt-20" : "opacity-0 -mt-20"
          }`}
          onClick={stopPropagation}
        >
          {/* Close button */}
          <button
            onClick={() => setIsOpen(false)}
            className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Close modal"
          >
            <X size={18} />
          </button>

          {/* Modal content */}
          <div className="p-6">
            {children}
          </div>
        </div>
      </div>
    </Portal>
  );
};

export default Modal;
